import PlatformEnum from '../enums/PlatformEnum';
import GaCategoryEnum from '../enums/GaCategoryEnum';
import GaEventEnum from '../enums/GaEventEnum';
import MetadataManager from '../utilities/MetadataManager';

type GoogleAnalyticsProps = {
	countryCode: string; // E.g. 'se'
	language: string; // E.g. 'sv'
	plannerName: string; // E.g. 'pax'
	platform: PlatformEnum; // Should be 'owfe', 'non-irw', or 'kiosk'
	dev: boolean; // True if is a development environment
};
const TEAM = 'ipexinsights';

declare global {
	// eslint-disable-next-line
	interface Window {
		sendEvent: (arg: any) => Promise<void>;
		analyticsInit: (arg: any) => Promise<void>;
	}
}

/**
 * Class for using Google Analytics and sending events.
 */
export default class GoogleAnalyticsTarget {
	/**
	 * Set up the Google Analytics integration based on given properties.
	 * Will initialize and send the first required event.
	 * Requires the Google Analytics script to be loaded in advance.
	 *
	 * @param props Parameters needed to initiate Google Analytics.
	 * @returns A Promise from window.sendEvent.
	 */
	public async setupGoogleAnalytics(props: GoogleAnalyticsProps): Promise<void> {
		if (this.checkGaEnabled()) {
			try {
				return await window
					.analyticsInit({
						countryCode: props.countryCode,
						languageCode: props.language,
						platform: props.platform,
						plannerName: props.plannerName,
						dev: props.dev
					})
					.then(() => {
						return this.sendPageViewEvent();
					});
			} catch (e) {
				// eslint-disable-next-line
				console.error(e);
			}
		}
	}

	/**
	 * Send a given set of data to Google Analytics.
	 *
	 * @param category The category the event belongs to.
	 * @param action The action being performed in the event.
	 * @param label Event label, optional.
	 * @param custom Parameters to include in the custom dimension.
	 */
	public async sendGoogleAnalyticsEvent(
		category: GaCategoryEnum,
		action: GaEventEnum,
		label?: string,
		custom?: {}
	): Promise<void> {
		if (this.checkGaEnabled()) {
			try {
				return await window.sendEvent({
					['event_category']: category,
					['event_action']: action,
					['event_label']: label,
					custom: {
						team: TEAM,
						...custom
					}
				});
			} catch (e) {
				// eslint-disable-next-line
				console.error(e);
			}
		}
	}

	/**
	 * Send an event to Google Analytics with a name and a set of parameters.
	 *
	 * @param name Name of the event to be sent.
	 * @param params Parameters to include in the event.
	 * @param custom Parameters to include in the custom dimension.
	 */
	public async sendGoogleAnalyticsEventWithParams(
		name: GaEventEnum,
		params: {},
		custom?: {}
	): Promise<void> {
		if (this.checkGaEnabled()) {
			try {
				return await window.sendEvent({
					name: name,
					params: params,
					custom: {
						team: TEAM,
						...custom
					}
				});
			} catch (e) {
				// eslint-disable-next-line
				console.error(e);
			}
		}
	}

	/**
	 * Check app metadata to see if GA is enabled.
	 *
	 * @returns True if GA is enabled, false if it's not, or if it's not defined.
	 */
	private checkGaEnabled(): boolean {
		const appMeta = MetadataManager.getApplicationMetadata();
		if (!('enableGa' in appMeta)) {
			return false;
		}
		if (appMeta.enableGa == null || appMeta.enableGa === false) {
			return false;
		}
		return true;
	}

	/**
	 * Send the initial page view event required for Google Analytics to work.
	 *
	 * @returns A Promise from window.sendEvent.
	 */
	private async sendPageViewEvent(): Promise<void> {
		if (this.checkGaEnabled()) {
			try {
				return await window.sendEvent({
					['event_category']: 'pageview',
					['page_path']: window.location.pathname,
					['page_location']: window.location.href,
					custom: {
						team: TEAM
					}
				});
			} catch (e) {
				// eslint-disable-next-line
				console.error(e);
			}
		}
	}
}
